// Plan 08-04 (refreshed Plan 30-04) — Runespring board cell layout helpers.
//
// Runespring topology (Phase 24+): the entire 6×6 field is a UNIFIED shared
// field. There is no private row. Cell count grows 36 → 48 → 60 across the
// match via flop (T6) and river (T11) reveals. Backend cell_idx layout is a
// flat row-major 6-col grid (cells 0..5 = back row, cells 30..35 = front row
// in the canonical projection).
//
// SCREEN COORDINATE CONVENTION:
//   The vertical flip (cellIdxToScreen when your_slot === 1) is preserved as
//   a UX affordance — the same end of the shared field always appears nearest
//   "you" regardless of which slot you occupy. This is purely cosmetic
//   orientation; both players see the same cells (Player A's back row and
//   Player B's back row are the same physical rows of the shared field).
//
// CELL_SIZE_PX is a logical token; PixiJS scales the stage to the canvas.

export const BOARD_COLS = 6
/**
 * The base Runespring field height (pre-flop). The field GROWS at runtime:
 * 36 cells (6 rows) → 48 (8 rows, T6 flop) → 60 (10 rows, T11 river). This
 * constant is the base/default only; live row count is derived per-render
 * from `board.length / BOARD_COLS` via {@link boardRows}, and threaded into
 * {@link cellIdxToScreen} as the `rows` argument so the vertical flip uses
 * the CURRENT height. Kept for back-compat (tests).
 */
export const BOARD_ROWS = 6
export const CELL_SIZE_PX = 64

/**
 * ARCA-749 (ARCA-717 WS1/WS3) — the FIXED 6×6 chess board: exactly 36 cells.
 * Client mirror of the backend `CHESS_CELLS` (apps/backend/src/match/schema.ts).
 *
 * After the grid decoupling (Option 2), the chess board NEVER grows or shifts
 * on a Flop/River reveal — only the word field deepens (36 → 48 → 60). So the
 * chess view's row count is the constant {@link CHESS_ROWS} (6), independent of
 * `playerView.board.length` (which now tracks the GROWING word field). Every
 * chess-view surface (PieceLayer, BoardLayer, MatchGrid, EffectLayer bursts)
 * sizes off these constants rather than the live board length.
 */
export const CHESS_CELLS = 36
/** Fixed chess-board row count (36 / 6 cols = 6). The chess grid never grows. */
export const CHESS_ROWS = CHESS_CELLS / BOARD_COLS

/**
 * Live row count of a Runespring field. The PlayerView.board is a flat
 * row-major array whose LENGTH is the current cell count (36 / 48 / 60), so
 * rows = length / cols. Used to drive the variable-height grid + the
 * vertical flip's row anchor as the board grows mid-match.
 */
export function boardRows(cellCount: number): number {
  return Math.max(1, Math.floor(cellCount / BOARD_COLS))
}

// ── Deepen-the-middle (MID) growth helpers ───────────────────────────────────
// The backend grows the field by INSERTING 2 rows at the vertical center and
// shifting the upper half outward (see apps/backend/.../turn.ts "5f.5"). These
// pure helpers let the frontend (a) identify the new center cohort for the
// spawn-in cascade and (b) give every cell a STABLE React key that survives the
// cell_idx shift so surviving sprites glide instead of remounting.
//
// NOTE: if the wire ever carries a stable cell id or an `is_new` flag
// (apps/backend/src/match/view/player-view.ts board map), prefer keying/flagging
// off that and retire these geometric helpers.

/**
 * The 12 cell indices inserted at the CENTER by the most recent reveal, for a
 * grown board of `boardLen` cells. Returns null pre-flop (36 cells). The new
 * rows occupy `start..end-1`: [18,30) at the flop (48 cells) and [24,36) at the
 * river (60 cells) — matching the backend's insert-at-`floor(rows/2)` contract.
 */
export function centerGrowWindow(
  boardLen: number,
): { start: number; end: number } | null {
  const rows = boardRows(boardLen)
  if (rows <= BOARD_ROWS) return null
  const start = (Math.floor(rows / 2) - 1) * BOARD_COLS
  return { start, end: start + 2 * BOARD_COLS }
}

/**
 * Project a cell's CURRENT cell_idx to its PERMANENT slot in the final 60-cell
 * (river-depth) grid. This value is invariant across BOTH reveals — an original
 * cell, a flop-born cell, and a river-born cell each map to a unique, stable
 * final slot — so it is a safe React key: surviving sprites keep the same key
 * across a reveal's cell_idx shift and therefore glide (instead of remounting
 * and teleporting). The projection composes the remaining reveal shifts:
 *   • pre-flop (≤36): apply the flop shift (idx≥18 → +12) then the river shift
 *     (resulting idx≥24 → +12)
 *   • post-flop (48): apply the river shift only (idx≥24 → +12)
 *   • river (60): already final
 * It is a bijection onto 0..59 over the cells that exist at each stage.
 */
export function finalCellKey(currentIdx: number, boardLen: number): number {
  const rows = boardRows(boardLen)
  let idx = currentIdx
  if (rows <= BOARD_ROWS) {
    // pre-flop → project through flop (threshold 18) then river (threshold 24)
    if (idx >= 18) idx += 2 * BOARD_COLS
    if (idx >= 24) idx += 2 * BOARD_COLS
  } else if (rows === 8) {
    // post-flop → project through river only (threshold 24)
    if (idx >= 24) idx += 2 * BOARD_COLS
  }
  return idx
}

export interface ScreenXY {
  /** 0..5 columns left→right */
  screen_x: number
  /** 0..5 rows top→bottom (0=top, 5=bottom) */
  screen_y: number
}

/**
 * Maps a Runespring shared-field cell_idx to screen coordinates.
 *
 * Applies a vertical flip when yourSlot === 0 so the caller's back row always
 * renders at the bottom of the visible field (UX convention — the same end of
 * the shared field always appears nearest "you" regardless of which slot you
 * occupy). yourSlot === 1 maps backend row directly to screen row.
 *
 * Post-Runespring (Phase 24+): all cells are on the unified shared field;
 * there is no private row. Cell count grows 36 → 48 → 60 across the match
 * via flop (T6) and river (T11) reveals — so the flip's row anchor MUST be
 * the CURRENT row count, passed by the caller via `rows`. With the flip
 * `rows - 1 - backendRow`, backend row 0 (the slot-0 player's near edge)
 * stays pinned to the screen BOTTOM as the field grows; the new flop/river
 * rows (backend rows 6..9) appear at the TOP (the opponent's far side).
 * Already-placed cells on low backend rows therefore keep their distance
 * from the player's near edge and do not re-flip when the board grows.
 *
 * `rows` defaults to the base {@link BOARD_ROWS} (6) for back-compat with
 * callers that only ever see the 36-cell field (e.g. unit tests).
 */
/**
 * The vertical row flip shared by EVERY board surface — the Pixi
 * {@link cellIdxToScreen}, the a11y DOM grid (MatchGrid), and the
 * WordTraceLayer polyline. For slot 0 the caller's near edge (backend row 0)
 * pins to the screen BOTTOM, so screen row = `rows - 1 - row`; slot 1 maps
 * directly. The function is its OWN INVERSE (`flipRow(flipRow(r)) === r`), so
 * it converts backend→screen AND screen→backend. SINGLE SOURCE OF TRUTH for
 * the flip — never re-derive `rows - 1 - row` anywhere else.
 */
export function flipRow(
  row: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): number {
  return yourSlot === 0 ? rows - 1 - row : row
}

/** Screen (row, col) → backend cell_idx, honoring the slot flip. */
export function screenToCellIdx(
  screenRow: number,
  screenCol: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): number {
  return flipRow(screenRow, yourSlot, rows) * BOARD_COLS + screenCol
}

/** Backend cell_idx → screen row, honoring the slot flip. */
export function cellIdxToScreenRow(
  cellIdx: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): number {
  return flipRow(Math.floor(cellIdx / BOARD_COLS), yourSlot, rows)
}

export function cellIdxToScreen(
  cellIdx: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): ScreenXY {
  return {
    screen_x: cellIdx % BOARD_COLS,
    screen_y: cellIdxToScreenRow(cellIdx, yourSlot, rows),
  }
}

/** Screen-pixel x of a cell (top-left of the cell rect). */
export function cellIdxToScreenX(
  cellIdx: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): number {
  return cellIdxToScreen(cellIdx, yourSlot, rows).screen_x * CELL_SIZE_PX
}

/** Screen-pixel y of a cell (top-left of the cell rect). */
export function cellIdxToScreenY(
  cellIdx: number,
  yourSlot: 0 | 1,
  rows: number = BOARD_ROWS,
): number {
  return cellIdxToScreen(cellIdx, yourSlot, rows).screen_y * CELL_SIZE_PX
}
